import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { X, Plus, Check } from 'lucide-react';
import { UserContext } from '../../UserContext';
import TagSelector from './TagSelector';
import { useSelector } from 'react-redux';
import { selectCurrentWorkspace } from '../../store/workspacesSlice';
import OfflineStatusBanner from '../../components/OfflineStatusBanner/OfflineStatusBanner';
import FormatTools from '../FormatTools/FormatTools';
import ItemPreview, { TagPill } from './ItemPreview';
import ItemPreviewSheet from './ItemPreviewSheet';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';

const getNonEmptyLineCount = (text) =>
  text.split('\n').map((l) => l.trim()).filter(Boolean).length;

const countBulletLines = (text) =>
  text.split('\n').filter((line) => /^\s*(•\s+|-\s+)/.test(line)).length;

const shouldShowBreakdownHint = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (trimmed.length > 200) return true;
  if (getNonEmptyLineCount(trimmed) > 2) return true;
  if (countBulletLines(trimmed) >= 3) return true;
  return false;
};

const createInitialConcepts = () => [
  { name: '', is_highlighted: true },
  { name: '', is_highlighted: false }
];

const conceptPlaceholders = [
  'orbits around the nucleus',
  'carries negative electric charge'
];

const AddItemModal = ({ 
  isOpen, 
  onClose, 
  onAdd, 
  workspaceId, 
  defaultBasketId,
  basketName,
  initialTopicName
}) => {
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const workspaceTags = currentWorkspace.tags || [];

  const { user } = useContext(UserContext);
  const [topicName, setTopicName] = useState(initialTopicName || '');
  const [concepts, setConcepts] = useState(createInitialConcepts);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);
  const [isPreviewSheetOpen, setIsPreviewSheetOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [highlightedConceptIndex, setHighlightedConceptIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [bulletPointModes, setBulletPointModes] = useState([]);
  const [numberedListModes, setNumberedListModes] = useState([]);
  const [listCounters, setListCounters] = useState([1]);
  const conceptRefs = useRef([]);
  const topicNameRef = useRef(null);
  const tagSelectorRef = useRef(null);
  const summaryRowRef = useRef(null);

  useEffect(() => {
    if (isOpen && topicNameRef.current) {
      setTimeout(() => {
        topicNameRef.current.focus();
        const length = topicNameRef.current.value.length;
        topicNameRef.current.setSelectionRange(length, length);
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    // Only focus the last concept when explicitly adding a new one
    if (concepts.length > 1) {
      const lastConceptIndex = concepts.length - 1;
      const textarea = conceptRefs.current[lastConceptIndex];
      if (textarea) {
        textarea.focus();
      }
    }
  }, [concepts.length]);

  const handleTagSelect = useCallback((tag) => {
    console.log('Selected tag:', tag);
    setSelectedTag(tag);
  }, []);

  const hasMarkedHard = concepts.some((c) => c.is_highlighted);
  const hasNonEmptyConcept = concepts.some((c) => c.name.trim() !== '');
  const hardDetailHelperText =
    'Mark the detail you keep forgetting. It is how you will place this topic.';

  const previewConcept =
    concepts.find((c) => c.is_highlighted && c.name.trim() !== '') ||
    concepts.find((c) => c.name.trim() !== '');
  const previewHardDetail = previewConcept ? previewConcept.name : '';

  const trimmedTopicName = topicName.trim();
  const conceptsLabelTitle =
    trimmedTopicName.length > 30
      ? `${trimmedTopicName.slice(0, 30)}…`
      : trimmedTopicName;

  const isPreviewEmpty =
    !trimmedTopicName && !previewHardDetail.trim() && !selectedTag;

  const handleOpenPreviewSheet = () => {
    captureConnectionsEvent('create_item_preview_opened', {
      source: isPreviewEmpty ? 'example' : 'summary_row',
    });
    setIsPreviewSheetOpen(true);
  };

  const handleClosePreviewSheet = useCallback(() => {
    setIsPreviewSheetOpen(false);
    summaryRowRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!topicName.trim()) return;
    if (!user || !user.id) {
      console.error('User not authenticated or user ID is missing');
      toast.error('Please log in to create an item.');
      return;
    }

    const conceptsToSave = concepts
      .filter((c) => c.name.trim() !== '')
      .map((c) => ({
        text: c.name.trim(),
        is_highlighted: c.is_highlighted
      }));

    if (conceptsToSave.length === 0) {
      return;
    }

    // If the hard mark was on a blank row (filtered out) or nothing is marked,
    // assign hard to the first non-empty concept so BSI can place the topic.
    if (!conceptsToSave.some((c) => c.is_highlighted)) {
      conceptsToSave[0].is_highlighted = true;
    }

    // Check for internet connection
    if (!navigator.onLine) {
      toast.error('No internet connection. Please check your connection and try again.');
      return;
    }

    setIsCreating(true);

    try {
      const tagToSubmit =
        selectedTag || tagSelectorRef.current?.commitPendingTag() || null;

      // Create the base topic data with proper tag handling
      const topicData = {
        name: topicName.trim(),
        basket_id: defaultBasketId,
        user_id: user.id,
        workspace_id: workspaceId,
        order_index: 0,
        concepts: conceptsToSave,
        tag_id: tagToSubmit?.id?.startsWith('new-') ? undefined : tagToSubmit?.id,
        tag: tagToSubmit?.id?.startsWith('new-') ? tagToSubmit : null
      };

      console.log('Submitting form with data:', topicData);

      if (typeof onAdd === 'function') {
        try {
          if (defaultBasketId) {
            await onAdd(topicData);
          } else {
            await onAdd({
              workspaceId,
              newTopic: topicData
            });
          }

          // Clear form fields EXCEPT the tag
          setTopicName('');
          setConcepts(createInitialConcepts());
          setIsPreviewSheetOpen(false);

          // Show success indicator
          setShowSuccessIndicator(true);
          setTimeout(() => {
            setShowSuccessIndicator(false);
            setIsCreating(false);
            if (topicNameRef.current) {
              topicNameRef.current.focus();
            }
          }, 1500);

        } catch (error) {
          // Handle specific error types
          if (error.message?.includes('network')) {
            toast.error('Network error. Please check your internet connection.');
          } else if (error.message?.includes('timeout')) {
            toast.error('Request timed out. Please try again.');
          } else if (error.code === '23505') { // Supabase unique constraint violation
            toast.error('A topic with this name already exists.');
          } else {
            toast.error(`Failed to create topic: ${error.message || 'Unknown error occurred'}`);
          }
          console.error('Error creating topic:', error);
          setIsCreating(false);
        }
      } else {
        console.error('onAdd is not a function');
        toast.error('System error: Unable to create topic');
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error(error.message || 'Failed to create topic. Please try again.');
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Clear all form fields
    setTopicName('');
    setConcepts(createInitialConcepts());
    setSelectedTag(null); // Clear the tag when closing the modal
    setHighlightedConceptIndex(0); // Reset highlighted index to 0
    setIsPreviewSheetOpen(false);
    onClose();
  };

  const handleConceptChange = (index, field, value) => {
    const updatedConcepts = concepts.map((c) => ({ ...c }));
    if (field === 'is_highlighted') {
      const concept = updatedConcepts[index];
      if (concept.is_highlighted && value === false) {
        concept.is_highlighted = false;
      } else if (value === true) {
        updatedConcepts.forEach((c, i) => {
          c.is_highlighted = i === index;
        });
      }
      const nextHardIndex = updatedConcepts.findIndex((c) => c.is_highlighted);
      setHighlightedConceptIndex(nextHardIndex);
    } else {
      updatedConcepts[index][field] = value;
    }
    setConcepts(updatedConcepts);
  };

  const addConcept = () => {
    const newIndex = concepts.length;
    setConcepts([...concepts, { name: '', is_highlighted: false }]);
    
    setTimeout(() => {
      if (conceptRefs.current[newIndex]) {
        conceptRefs.current[newIndex].focus();
      }
    }, 0);
  };

  const removeConcept = (index) => {
    const updatedConcepts = concepts.filter((_, i) => i !== index);
    setConcepts(updatedConcepts);
  };

  const formatSelection = (index, type, e) => {
    e?.preventDefault();
    const textarea = conceptRefs.current[index];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    if (start === end) {
      // No selection, add formatting at cursor position
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      let formattedText;
      switch (type) {
        case 'bullet':
          formattedText = `${textBeforeCursor}• ${textAfterCursor}`;
          break;
        case 'number':
          const newCounter = (listCounters[index] || 0) + 1;
          formattedText = `${textBeforeCursor}${newCounter}. ${textAfterCursor}`;
          const newCounters = [...listCounters];
          newCounters[index] = newCounter;
          setListCounters(newCounters);
          break;
        case 'indent':
          formattedText = `${textBeforeCursor}    ${textAfterCursor}`;
          break;
        default:
          formattedText = text;
      }
      
      handleConceptChange(index, 'name', formattedText);
      
      // Set cursor position after the inserted formatting
      setTimeout(() => {
        const newPosition = cursorPosition + (type === 'bullet' ? 2 : type === 'number' ? 3 : 4);
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
      return;
    }

    // Get selected text and split into lines
    const selectedText = text.substring(start, end);
    const lines = selectedText.split('\n');
    
    // Format each line based on type
    const formattedLines = lines.map((line, i) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';
      
      switch (type) {
        case 'bullet':
          return line.startsWith('• ') ? line : `• ${trimmedLine}`;
        case 'number':
          return line.match(/^\d+\. /) ? line : `${i + 1}. ${trimmedLine}`;
        case 'indent':
          return line.startsWith('    ') ? line : `    ${trimmedLine}`;
        default:
          return line;
      }
    });

    // Combine the formatted lines
    const formattedText = formattedLines.join('\n');
    
    // Replace the selected text with formatted text
    const newText = text.substring(0, start) + formattedText + text.substring(end);
    handleConceptChange(index, 'name', newText);

    // Maintain focus and update selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start,
        start + formattedText.length
      );
    }, 0);
  };

  const addBulletPoint = (index) => {
    const newModes = [...bulletPointModes];
    newModes[index] = !newModes[index];
    setBulletPointModes(newModes);
    
    const newNumberedModes = [...numberedListModes];
    newNumberedModes[index] = false;
    setNumberedListModes(newNumberedModes);

    if (newModes[index]) {
      const textarea = conceptRefs.current[index];
      if (!textarea) return;

      const cursorPosition = textarea.selectionStart;
      const text = textarea.value;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      const formattedText = `${textBeforeCursor}• ${textAfterCursor}`;
      handleConceptChange(index, 'name', formattedText);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
      }, 0);
    }
  };

  const addNumberedList = (index) => {
    const newModes = [...numberedListModes];
    newModes[index] = !newModes[index];
    setNumberedListModes(newModes);
    
    const newBulletModes = [...bulletPointModes];
    newBulletModes[index] = false;
    setBulletPointModes(newBulletModes);

    if (newModes[index]) {
      const textarea = conceptRefs.current[index];
      if (!textarea) return;

      const cursorPosition = textarea.selectionStart;
      const text = textarea.value;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      const newCounter = 1;
      const formattedText = `${textBeforeCursor}${newCounter}. ${textAfterCursor}`;
      handleConceptChange(index, 'name', formattedText);
      
      const newCounters = [...listCounters];
      newCounters[index] = newCounter;
      setListCounters(newCounters);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPosition + 3, cursorPosition + 3);
      }, 0);
    }
  };

  const addIndentation = (index) => formatSelection(index, 'indent', { preventDefault: () => {} });
  const addParagraph = (index) => formatSelection(index, 'paragraph', { preventDefault: () => {} });

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      if (bulletPointModes[index]) {
        e.preventDefault();
        insertFormatting(index, '\n• ');
      } else if (numberedListModes[index]) {
        e.preventDefault();
        const newCounters = [...listCounters];
        newCounters[index] = (newCounters[index] || 0) + 1;
        setListCounters(newCounters);
        insertFormatting(index, `\n${newCounters[index]}. `);
      }
    }
  };

  const insertFormatting = (index, startChar, endChar = '') => {
    const textarea = conceptRefs.current[index];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const beforeText = text.substring(0, start);
    const selectedText = text.substring(start, end);
    const afterText = text.substring(end);

    const newText = beforeText + startChar + selectedText + endChar + afterText;
    handleConceptChange(index, 'name', newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + startChar.length, end + startChar.length);
    }, 0);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 sm:p-6">
      <div className="bg-neutral-50 rounded-xl border-2 border-neutral-900 shadow-xl p-5 sm:p-6 
                    w-full max-w-3xl max-h-[90vh] overflow-y-auto relative">
        <OfflineStatusBanner />
        {showSuccessIndicator && (
          <div className="absolute inset-0 flex items-center justify-center bg-emerald-600 bg-opacity-90 z-[60]">
            <div className="bg-white rounded-xl border-2 border-neutral-900 p-4 flex items-center shadow-lg">
              <Check size={24} className="text-emerald-600 mr-2" />
              <span className="text-neutral-900 font-medium">Topic created successfully</span>
            </div>
          </div>
        )}
        <div className="flex justify-between items-start mb-4 gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-500 mb-1">Step 1 of 2</p>
            <h2 className="text-xl font-bold text-neutral-900">Create Item</h2>
            {defaultBasketId && basketName ? (
              <p className="text-sm text-neutral-500 mt-1 truncate">
                Adding to {basketName}
              </p>
            ) : null}
          </div>
          <button 
            onClick={handleClose} 
            className="text-neutral-900 hover:text-neutral-700 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] md:gap-8">
            <div className="space-y-6">
              <div>
                <label htmlFor="topicName" className="block text-sm font-semibold text-neutral-800 mb-2">
                  Topic Name
                </label>
                <input
                  id="topicName"
                  ref={topicNameRef}
                  type="text"
                  value={topicName}
                  onChange={(e) => setTopicName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border-2 border-neutral-900 rounded-xl text-neutral-900 
                           placeholder-neutral-400"
                  placeholder="e.g., Electron"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-800 mb-2">
                  {trimmedTopicName ? (
                    <>
                      What do you keep forgetting about{' '}
                      <span className="font-bold text-cranberry-700">{conceptsLabelTitle}</span>?
                    </>
                  ) : (
                    'Concepts'
                  )}
                </label>
                <p className="text-xs text-neutral-500 italic leading-relaxed mb-3">
                  Mark the one you&apos;d blank on in an exam, or keep getting wrong in practice questions.
                </p>

                <div className="space-y-4">
                  {concepts.map((concept, index) => (
                    <div key={index}>
                      <div className="rounded-xl border-2 border-neutral-900 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-neutral-300">
                        <textarea
                          id={`concept-text-${index}`}
                          ref={el => conceptRefs.current[index] = el}
                          value={concept.name}
                          onChange={(e) => handleConceptChange(index, 'name', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          className="block w-full px-4 py-3 bg-white border-0 text-neutral-900 placeholder-neutral-400 resize-y min-h-[88px] focus:outline-none focus:ring-0"
                          placeholder={conceptPlaceholders[index] || `Concept ${index + 1}`}
                          aria-describedby={
                            shouldShowBreakdownHint(concept.name)
                              ? `breakdown-hint-${index}`
                              : undefined
                          }
                        />
                        <div
                          className={`flex flex-wrap items-center gap-2 border-t border-neutral-200 px-3 py-2 ${
                            concept.is_highlighted ? 'bg-amber-50' : 'bg-neutral-50'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => handleConceptChange(index, 'is_highlighted', !concept.is_highlighted)}
                            className={`
                              text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap flex-shrink-0
                              ${concept.is_highlighted
                                ? 'bg-amber-200 text-amber-800 border-amber-300'
                                : 'border-neutral-300 text-neutral-500 hover:border-amber-300 hover:text-amber-700'
                              }
                            `}
                            aria-label={concept.is_highlighted ? 'Unmark as hard' : 'Mark as hard'}
                          >
                            {concept.is_highlighted ? 'hard' : 'mark as hard'}
                          </button>
                          <div className="ml-auto flex items-center gap-1">
                            <FormatTools
                              className="flex space-x-1"
                              onBulletPoint={() => addBulletPoint(index)}
                              onNumberedList={() => addNumberedList(index)}
                              onIndentation={() => addIndentation(index)}
                              onParagraph={() => addParagraph(index)}
                              bulletPointActive={bulletPointModes[index]}
                              numberedListActive={numberedListModes[index]}
                              conceptIndex={index}
                              onFormatSelection={(type, e) => formatSelection(index, type, e)}
                            />
                            {concepts.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeConcept(index)}
                                className="p-1.5 rounded-md text-neutral-400 hover:text-cranberry-700 hover:bg-neutral-200 transition-colors"
                                aria-label="Remove concept"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {shouldShowBreakdownHint(concept.name) && (
                        <div
                          id={`breakdown-hint-${index}`}
                          role="note"
                          className="mt-2 rounded-lg border border-neutral-200 bg-neutral-100 px-3 py-2 text-xs text-neutral-700 leading-relaxed"
                        >
                          This looks like multiple details. Narrow it down to the one you find most
                          difficult or high yield, and add the others as separate concepts.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {!hasMarkedHard && (
                  <p
                    id="hard-detail-helper"
                    className="mt-3 text-xs text-neutral-500 leading-relaxed"
                  >
                    {hardDetailHelperText}
                  </p>
                )}
                <button
                  type="button"
                  onClick={addConcept}
                  className="mt-3 flex items-center text-emerald-600 hover:text-emerald-700 
                           transition-colors font-medium"
                >
                  <Plus size={16} className="mr-1" /> Add Concept
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-800 mb-2">Tag</label>
                <TagSelector
                  ref={tagSelectorRef}
                  onTagSelect={handleTagSelect}
                  selectedTag={selectedTag}
                  existingTags={workspaceTags}
                />
              </div>

              <button
                type="button"
                ref={summaryRowRef}
                onClick={handleOpenPreviewSheet}
                aria-haspopup="dialog"
                className="md:hidden w-full flex items-center justify-between gap-3 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-left"
              >
                {isPreviewEmpty ? (
                  <>
                    <span>See an example item</span>
                    <span aria-hidden="true">›</span>
                  </>
                ) : (
                  <>
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={`truncate ${
                          trimmedTopicName
                            ? 'font-semibold text-neutral-900'
                            : 'text-neutral-400'
                        }`}
                      >
                        {trimmedTopicName || 'Topic name'}
                      </span>
                      <TagPill tag={selectedTag} />
                    </span>
                    <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                      See preview ›
                    </span>
                  </>
                )}
              </button>
            </div>
            <aside className="hidden md:block md:sticky md:top-0 md:self-start">
              <ItemPreview
                name={topicName}
                tag={selectedTag}
                hardDetail={previewHardDetail}
                showBasketHint={!defaultBasketId}
              />
            </aside>
          </div>
          <div className="flex flex-wrap justify-end gap-3 pt-6">
            {!isCreating && (
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-white text-neutral-900 rounded-xl border-2 border-neutral-900 
                         font-medium hover:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 
                       font-medium hover:bg-cranberry-600 transition-colors disabled:opacity-70 
                       disabled:cursor-not-allowed"
              disabled={!user || !user.id || isCreating || !hasNonEmptyConcept || !topicName.trim()}
              title={!hasMarkedHard ? hardDetailHelperText : undefined}
              aria-describedby={!hasMarkedHard ? 'hard-detail-helper' : undefined}
            >
              {isCreating ? 'Creating...' : 'Next: put it in a basket'}
            </button>
          </div>
        </form>
        <ItemPreviewSheet
          isOpen={isPreviewSheetOpen}
          onClose={handleClosePreviewSheet}
        >
          <ItemPreview
            hideLabel
            name={topicName}
            tag={selectedTag}
            hardDetail={previewHardDetail}
            showBasketHint={!defaultBasketId}
          />
        </ItemPreviewSheet>
      </div>
    </div>
  );
};

export default React.memo(AddItemModal);
